import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [status, setStatus] = useState(token ? "verifying" : "error");
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setStatus("success");
        toast.success("Email verified! Welcome to InfluencerCollab.");
        // Full page load so AuthProvider re-bootstraps with the freshly-set session cookie.
        const target = data.profile_complete ? "/dashboard" : "/onboarding";
        setTimeout(() => window.location.replace(target), 1200);
      } catch (e) {
        setStatus("error");
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center p-5">
      <div className="grain" />
      <div className="hero-glow absolute top-0 inset-x-0 h-[500px] z-0 pointer-events-none" />
      <div className="relative z-10 w-full max-w-md rounded-3xl border border-border bg-surface p-8 text-center">
        <div className="flex justify-center mb-6"><Logo to="/" /></div>

        {status === "verifying" && (
          <div data-testid="verify-loading">
            <Loader2 className="h-10 w-10 text-brand-pink mx-auto mb-4 animate-spin" />
            <h1 className="font-heading font-black text-2xl tracking-tight mb-2">Verifying your email…</h1>
            <p className="text-sm text-muted-foreground">Hang tight while we activate your account.</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="verify-success">
            <CheckCircle2 className="h-12 w-12 text-neon-lime mx-auto mb-4" />
            <h1 className="font-heading font-black text-2xl tracking-tight mb-2">You're verified!</h1>
            <p className="text-sm text-muted-foreground">Taking you to finish setting up your account…</p>
          </div>
        )}

        {status === "error" && (
          <div data-testid="verify-error">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h1 className="font-heading font-black text-2xl tracking-tight mb-2">Link invalid or expired</h1>
            <p className="text-sm text-muted-foreground mb-6">This verification link is no longer valid. Please sign in and request a new one.</p>
            <Button onClick={() => navigate("/")} data-testid="verify-home-btn" className="rounded-full bg-brand-gradient text-white font-bold h-11 px-8">
              Back to home
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
